// Christmas special raffle — holiday themed gift box raffle
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Connection, PublicKey, SystemProgram, Transaction, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { shortAddr } from '../utils/helpers';
import './Christmas.css';

/* global BigInt */

// Base image CID for tickets / reference
const PURCHASE_NFT_CID = 'bafybeifwmxlyjeptl7dgsk5dxchxrthhe76ehnz3lvt6tnxnlsxmqcuo2i';
// Winner / mystery box for Christmas
const WINNER_NFT_CID = 'bafybeicdsxlus4bkfdjnqhl4bzv6pimzlae6muz2znumelwfoa3zas5dgq';

// Fixed recipients
const SOLANA_RECEIVER = '3qhCfjcZBbApTyYxtuZp8zSU3V1xK3ex6AWfGNTtiYzh';
const FIXED_ADMIN_WALLET = '0x8bfc802e8decc49b63158b9ec4122e04a22d819c';

const LOCAL_KEY = 'christmas:raffleTickets';
const RESULTS_KEY = 'christmas:raffleResults';
const NFT_STOCK_KEY = 'christmas:nftStock';

// Preferred IPFS gateways
const PINATA_GATEWAY = 'https://turquoise-many-mouse-901.mypinata.cloud/ipfs';
const PUBLIC_IPFS_GATEWAY = 'https://ipfs.io/ipfs';

// Raffle configuration
const RAFFLE_CONFIG = {
  priceUSD: 49.99,
  prizeValue: 'Christmas Special',
  prizeItems: [
    'Private lesson',
    'T-shirt',
    '30 days unlimited training',
  ],
  maxTicketsDisplay: 12,
};

// ------------------ Utility Functions ------------------
const ipfsUrl = (cid, gateway = PINATA_GATEWAY) => {
  if (!cid) return '';
  const clean = cid.replace(/^\/?ipfs\//, '');
  return `${gateway}/${clean}`;
};

const makeId = (prefix = 'xmas') => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch (e) {}
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const toWeiHex = (amountEth) => {
  try {
    const s = String(amountEth || '0');
    const [whole, frac = ''] = s.split('.');
    const fracPadded = (frac + '000000000000000000').slice(0, 18);
    const wei = BigInt(whole || '0') * 10n ** 18n + BigInt(fracPadded.replace(/^0+/, '') || '0');
    return '0x' + wei.toString(16);
  } catch (e) {
    return null;
  }
};

const waitForReceipt = async (txHash, timeout = 120000, interval = 2500) => {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    try {
      const receipt = await window.ethereum.request({
        method: 'eth_getTransactionReceipt',
        params: [txHash],
      });
      if (receipt) return receipt;
    } catch (e) {}
    await new Promise((r) => setTimeout(r, interval));
  }
  return null;
};

// ------------------ NFT Stock Management ------------------
const useNFTStock = () => {
  const loadNFTStock = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(NFT_STOCK_KEY));
      if (Array.isArray(saved)) {
        return saved;
      }
    } catch (e) {}
    return [];
  };

  const saveNFTStock = (stock) => {
    try {
      localStorage.setItem(NFT_STOCK_KEY, JSON.stringify(stock));
      window.dispatchEvent(new CustomEvent('christmas:stock'));
    } catch (e) {}
  };

  const getNextNFT = (stock) => stock.find((n) => !n.sold);

  const markNFTAsSold = (stock, id) => {
    const idx = stock.findIndex((n) => n.id === id);
    if (idx >= 0) {
      const newStock = [...stock];
      newStock[idx] = { ...newStock[idx], sold: true };
      saveNFTStock(newStock);
      window.dispatchEvent(new CustomEvent('christmas:stock-sold', { detail: { id } }));
      return newStock;
    }
    return stock;
  };

  return { loadNFTStock, saveNFTStock, getNextNFT, markNFTAsSold };
};

// ------------------ Ticket Management ------------------
const useTicketManagement = () => {
  const loadTickets = () => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };

  const saveTicket = (ticket) => {
    try {
      const tickets = loadTickets();
      const newTickets = [ticket, ...tickets];
      localStorage.setItem(LOCAL_KEY, JSON.stringify(newTickets));
      window.dispatchEvent(new CustomEvent('christmas:ticket', { detail: ticket }));
      return newTickets;
    } catch (e) {
      return loadTickets();
    }
  };

  const saveDrawResult = (result) => {
    try {
      localStorage.setItem(RESULTS_KEY, JSON.stringify(result));
      window.dispatchEvent(new CustomEvent('christmas:draw', { detail: result }));
    } catch (e) {}
  };

  return { loadTickets, saveTicket, saveDrawResult };
};

// ------------------ API Calls ------------------
const useAPI = () => {
  const fetchEthPriceUSD = async () => {
    try {
      // Primary: CoinGecko
      const res = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd'
      );
      const js = await res.json();
      const price = js?.ethereum?.usd || null;
      if (price) {
        try { localStorage.setItem('ethPriceUSD', JSON.stringify({ price, ts: Date.now() })); } catch (e) {}
        return price;
      }
    } catch (e) {}

    try {
      // Fallback: Coinbase public spot price
      const res2 = await fetch('https://api.coinbase.com/v2/prices/ETH-USD/spot');
      if (res2.ok) {
        const j2 = await res2.json();
        const amt = parseFloat(j2?.data?.amount);
        if (!Number.isNaN(amt) && amt > 0) {
          try { localStorage.setItem('ethPriceUSD', JSON.stringify({ price: amt, ts: Date.now() })); } catch (e) {}
          return amt;
        }
      }
    } catch (e) {}

    // Last resort: use cached price from localStorage
    try {
      const cached = JSON.parse(localStorage.getItem('ethPriceUSD') || 'null');
      if (cached && cached.price) return cached.price;
    } catch (e) {}

    return null;
  };

  const fetchSolPriceUSD = async () => {
    try {
      const res = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd'
      );
      const js = await res.json();
      return js?.solana?.usd || null;
    } catch (e) {
      return null;
    }
  };

  const syncOrderToAPI = async (orderPayload) => {
    try {
      const resp = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload),
      });
      return resp.ok;
    } catch (e) {
      return false;
    }
  };

  const mintNFT = async (mintData) => {
    try {
      const res = await fetch('/api/raffle/mint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mintData),
      });
      if (res.ok) {
        const json = await res.json();
        return json?.mintedTokenId || null;
      }
    } catch (e) {}
    return null;
  };

  return { fetchEthPriceUSD, fetchSolPriceUSD, syncOrderToAPI, mintNFT };
};

// ------------------ Main Component ------------------
export default function Christmas({ onClose = () => {}, adminWallet = '', inline = false, embedded = false }) {
  // Hooks
  const nftStock = useNFTStock();
  const ticketManager = useTicketManagement();
  const api = useAPI();

  // State
  const [tickets, setTickets] = useState(ticketManager.loadTickets());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [currentTicket, setCurrentTicket] = useState(null);
  const [hasWallet, setHasWallet] = useState(!!window?.ethereum);
  const [walletAddress, setWalletAddress] = useState('');
  const [nftStockState, setNftStockState] = useState(nftStock.loadNFTStock());
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // UI State
  const [imgFailed, setImgFailed] = useState(false);
  const [exampleImgFailed, setExampleImgFailed] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [exampleCardFlipped, setExampleCardFlipped] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('buy');
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [isCompactHeight, setIsCompactHeight] = useState(false);
  const [chainId, setChainId] = useState(window.ethereum?.chainId || null);
  const [solPrice, setSolPrice] = useState(null);
  const [priceUpdateNotice, setPriceUpdateNotice] = useState(null);
  const [showAdminMenu, setShowAdminMenu] = useState(false);

  // Refs
  const prevSolPriceRef = useRef(null);
  const panelRef = useRef(null);

  // ------------------ Computed Values ------------------
  const isLocalAdmin = useMemo(() => {
    try {
      const stored = localStorage.getItem('bragabjj_admin_status');
      if (stored === '1' || stored === 'true') return true;
    } catch (e) {}
    return !!(adminWallet && walletAddress && walletAddress === adminWallet.toLowerCase());
  }, [adminWallet, walletAddress]);

  const availableBoxes = useMemo(() => 
    nftStockState.filter((n) => !n.sold).length,
    [nftStockState]
  );

  const totalBoxes = useMemo(() => 
    nftStockState.length,
    [nftStockState]
  );

  const isStandalone = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('standalone') === '1' || sp.get('standalone') === 'true';
    } catch (e) {
      return false;
    }
  }, []);

  // ------------------ Effects ------------------
  useEffect(() => {
    // Load initial data and set up listeners
    const handler = () => setTickets(ticketManager.loadTickets());
    const stockHandler = () => setNftStockState(nftStock.loadNFTStock());
    
    window.addEventListener('christmas:ticket', handler);
    window.addEventListener('christmas:stock', stockHandler);
    window.addEventListener('storage', (ev) => {
      if (ev.key === NFT_STOCK_KEY) setNftStockState(nftStock.loadNFTStock());
    });

    return () => {
      window.removeEventListener('christmas:ticket', handler);
      window.removeEventListener('christmas:stock', stockHandler);
    };
  }, [ticketManager, nftStock]);

  useEffect(() => {
    // Device detection
    const detectDevice = () => {
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(touch);
    };
    detectDevice();
  }, []);

  useEffect(() => {
    // Viewport and wallet detection
    const detectViewport = () => {
      const ua = navigator.userAgent.toLowerCase();
      const mm = window.matchMedia('(max-width: 768px)');
      const prefersCoarse = window.matchMedia('(pointer: coarse)').matches;
      const mobileUa = /mobi|android|iphone|ipad|ipod|windows phone/.test(ua);
      const inApp = /metamask|facebook|instagram|twitter|line|wechat|wv|wv\)/i.test(ua) || 
                    !!window?.webkit?.messageHandlers;
      
      const isM = mm.matches || mobileUa || prefersCoarse;
      setIsMobile(isM);
      setIsInAppBrowser(inApp && isM);
      
      const vh = window.innerHeight;
      setIsCompactHeight(vh < 700 && isM);
      document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
    };

    const checkWallet = () => setHasWallet(!!window?.ethereum);
    const handleAccounts = (accounts) => {
      setWalletAddress(accounts?.[0]?.toLowerCase() || '');
    };
    const handleChainChanged = (chain) => setChainId(chain);

    // Set up event listeners
    window.addEventListener('resize', detectViewport);
    window.addEventListener('orientationchange', detectViewport);
    window.addEventListener('focus', checkWallet);
    
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccounts);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    detectViewport();
    checkWallet();

    // Initial wallet check
    if (window.ethereum?.request) {
      window.ethereum.request({ method: 'eth_accounts' })
        .then(accounts => handleAccounts(accounts))
        .catch(() => {});
    }

    return () => {
      window.removeEventListener('resize', detectViewport);
      window.removeEventListener('orientationchange', detectViewport);
      window.removeEventListener('focus', checkWallet);
      
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccounts);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  useEffect(() => {
    // Fetch Solana price
    let mounted = true;
    const fetchPrice = async () => {
      const price = await api.fetchSolPriceUSD();
      if (mounted && price) {
        const prev = prevSolPriceRef.current;
        if (prev && prev !== price) {
          setPriceUpdateNotice({
            oldPrice: prev,
            newPrice: price,
            timestamp: new Date().toISOString(),
          });
          setTimeout(() => setPriceUpdateNotice(null), 12000);
        }
        prevSolPriceRef.current = price;
        setSolPrice(price);
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [api]);

  useEffect(() => {
    // Handle mobile input focus
    if (!isMobile || !panelRef.current) return;

    const inputs = Array.from(panelRef.current.querySelectorAll('input, textarea, select'));
    const focusHandler = (ev) => {
      ev.target.classList.add('input-focus-scroll');
      setTimeout(() => ev.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
    };
    const blurHandler = (ev) => ev.target.classList.remove('input-focus-scroll');

    inputs.forEach((input) => {
      input.addEventListener('focus', focusHandler, { passive: true });
      input.addEventListener('blur', blurHandler, { passive: true });
      input.addEventListener('touchstart', () => input.focus(), { passive: true });
    });

    return () => {
      inputs.forEach((input) => {
        input.removeEventListener('focus', focusHandler);
        input.removeEventListener('blur', blurHandler);
      });
    };
  }, [isMobile]);

  // ------------------ Business Logic ------------------
  const getChainLabel = useCallback((id) => {
    if (!id) return 'Unknown';
    const norm = String(id).toLowerCase();
    const chains = {
      '0x1': 'Ethereum',
      '0x3': 'Ropsten',
      '0x4': 'Rinkeby',
      '0x5': 'Goerli',
      '0x89': 'Polygon',
      '0x13881': 'Mumbai',
      '0x38': 'BSC',
    };
    return chains[norm] || `Chain ${id}`;
  }, []);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      setMessage('MetaMask not available');
      setHasWallet(false);
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const addr = accounts?.[0];
      if (addr) {
        setWalletAddress(addr.toLowerCase());
        setHasWallet(true);
        setMessage('Wallet connected');
      }
    } catch (e) {
      console.warn('connectWallet failed', e);
      setMessage('Connection failed');
    }
  }, []);

  const disconnectWallet = useCallback(() => {
    setWalletAddress('');
    setMessage('Wallet disconnected');
  }, []);

  const buyTicket = useCallback(async () => {
    if (!firstName || !lastName) {
      setMessage('Please enter your first and last name before purchasing');
      return;
    }

    if (!window.ethereum?.request) {
      setMessage('MetaMask not available — please install or participate as guest');
      setHasWallet(false);
      return;
    }

    setLoading(true);
    setMessage('Preparing payment...');

    try {
      // Get ETH price and calculate amount
      const ethUSD = await api.fetchEthPriceUSD();
      if (!ethUSD) {
        setMessage('Could not determine ETH price right now — please try again shortly');
        setLoading(false);
        return;
      }

      const ethAmount = RAFFLE_CONFIG.priceUSD / ethUSD;
      if (Number.isNaN(ethAmount) || ethAmount <= 0) {
        setMessage('Invalid ETH amount calculated');
        setLoading(false);
        return;
      }

      // Request wallet connection
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const from = accounts?.[0] || '';
      const fromLower = from.toLowerCase();
      setWalletAddress(fromLower);

      // Validate admin wallet
      const dest = (FIXED_ADMIN_WALLET || adminWallet || '').toLowerCase();
      if (!dest) {
        setMessage('Admin wallet not configured');
        setLoading(false);
        return;
      }

      // Confirm transaction
      const ethDisplay = ethAmount.toFixed(6);
      const confirmText = `Send ${ethDisplay} ETH (~$${RAFFLE_CONFIG.priceUSD}) to ${shortAddr(dest)} to purchase a Christmas box?`;
      if (!window.confirm(confirmText)) {
        setMessage('Payment cancelled');
        setLoading(false);
        return;
      }

      // Ensure correct chain
      const DESIRED_CHAIN = '0x1';
      const currentChain = window.ethereum.chainId;
      if (currentChain && currentChain !== DESIRED_CHAIN) {
        try {
          setMessage('Switching to Ethereum mainnet...');
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: DESIRED_CHAIN }],
          });
          await new Promise(r => setTimeout(r, 400));
        } catch (switchErr) {
          const proceed = window.confirm('Your wallet is not on Ethereum mainnet. Continue anyway?');
          if (!proceed) {
            setMessage('Please switch to Ethereum mainnet');
            setLoading(false);
            return;
          }
        }
      }

      // Prepare and send transaction
      const valueHex = toWeiHex(ethAmount);
      if (!valueHex) {
        setMessage('Could not compute payment amount');
        setLoading(false);
        return;
      }

      const txParams = {
        from,
        to: dest,
        value: valueHex,
        chainId: DESIRED_CHAIN,
      };

      const txHash = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });

      setMessage('Transaction submitted — waiting for confirmation...');
      const receipt = await waitForReceipt(txHash).catch(() => null);

      if (receipt?.status !== '0x1') {
        setMessage('Transaction failed or pending');
        setLoading(false);
        return;
      }

      setMessage('Transaction confirmed — issuing ticket...');

      // Get next available NFT
      const nextNFT = nftStock.getNextNFT(nftStockState);
      if (!nextNFT) {
        setMessage('All boxes sold out');
        setLoading(false);
        return;
      }

      // Create ticket
      const ticket = {
        id: makeId('xmas'),
        owner: fromLower,
        txHash,
        ts: new Date().toISOString(),
        nft: nextNFT.cid,
        nftId: nextNFT.id,
        paymentMethod: 'metamask',
        firstName,
        lastName,
        priceUSD: RAFFLE_CONFIG.priceUSD,
        paymentStatus: 'paid',
      };

      // Update NFT stock
      const updatedStock = nftStock.markNFTAsSold(nftStockState, nextNFT.id);
      setNftStockState(updatedStock);

      // Save ticket
      const updatedTickets = ticketManager.saveTicket(ticket);
      setTickets(updatedTickets);
      setCurrentTicket(ticket);
      setMessage('Christmas box purchased!');

      // Sync to backend
      try {
        const orderPayload = {
          id: ticket.id,
          items: [{
            id: nextNFT.id,
            name: nextNFT.name || 'christmas-box',
            qty: 1,
            price: RAFFLE_CONFIG.priceUSD,
          }],
          buyer: {
            firstName,
            lastName,
            wallet: fromLower,
          },
          total: RAFFLE_CONFIG.priceUSD,
          createdAt: ticket.ts,
          raffleId: 'christmas_2025',
          walletAddress: fromLower,
          paymentProvider: 'metamask',
          paymentStatus: 'paid',
          raffleNFTId: nextNFT.id,
        };

        await api.syncOrderToAPI(orderPayload);

        const mintedTokenId = await api.mintNFT({
          wallet: fromLower,
          cid: nextNFT.cid,
          edition: nextNFT.id,
          ticketId: ticket.id,
          txHash,
          ts: ticket.ts,
          firstName,
          lastName,
        });

        if (mintedTokenId) {
          ticket.mintedTokenId = mintedTokenId;
          ticketManager.saveTicket(ticket);
          setTickets(ticketManager.loadTickets());
        }
      } catch (error) {
        console.warn('Backend sync failed:', error);
      }

      setTimeout(() => setMessage('You received an entry. Good luck!'), 1600);
    } catch (error) {
      console.warn('Purchase failed:', error);
      setMessage('Payment failed or cancelled');
    } finally {
      setLoading(false);
    }
  }, [firstName, lastName, nftStockState, adminWallet, api, nftStock, ticketManager]);

  const runDraw = useCallback(async () => {
    if (!isLocalAdmin) return;

    const pin = prompt('Enter admin PIN to run Christmas draw');
    if (!pin) return;

    setMessage('Verifying PIN...');
    const verified = await verifyAdminPin(pin);
    if (!verified) {
      setMessage('Admin PIN verification failed');
      return;
    }

    const pool = tickets;
    if (!pool.length) {
      setMessage('No tickets to draw');
      return;
    }

    setMessage('Spinning the roulette...');
    
    return new Promise((resolve) => {
      let i = 0;
      const spins = 40 + Math.floor(Math.random() * 40);
      const interval = setInterval(() => {
        const idx = Math.floor(Math.random() * pool.length);
        setCurrentTicket(pool[idx]);
        i += 1;

        if (i >= spins) {
          clearInterval(interval);
          const winner = pool[Math.floor(Math.random() * pool.length)];
          setCurrentTicket(winner);

          const result = {
            winner,
            ts: new Date().toISOString(),
            poolSize: pool.length,
          };

          ticketManager.saveDrawResult(result);
          setMessage(`Winner: ${shortAddr(winner.owner)} — ticket ${winner.id}`);

          // Announce winner NFT
          if (winner.owner && !winner.owner.startsWith('guest:')) {
            api.mintNFT({
              wallet: winner.owner,
              cid: WINNER_NFT_CID,
              edition: 'winner',
              ticketId: winner.id,
            }).catch(console.error);
          }

          resolve(result);
        }
      }, 80);
    });
  }, [tickets, isLocalAdmin, api, ticketManager]);

  const verifyAdminPin = async (pin) => {
    try {
      const res = await fetch('/api/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, wallet: adminWallet }),
      });
      return res.ok;
    } catch (e) {
      return window.confirm('Could not verify PIN with server. Proceed anyway?');
    }
  };

  const clearTickets = useCallback(() => {
    if (!isLocalAdmin) return;
    localStorage.removeItem(LOCAL_KEY);
    setTickets([]);
    setMessage('Cleared all tickets');
  }, [isLocalAdmin]);

  // ------------------ Render Functions ------------------
  const renderWalletStatus = () => (
    <div className="bf-wallet-status" role="status" aria-label="wallet-status">
      <div className="bf-wallet-fox" aria-hidden>🎄</div>
      <div className="bf-wallet-text">
        {walletAddress ? shortAddr(walletAddress) : hasWallet ? 'Not connected' : 'MetaMask not found'}
      </div>
      <div className={`bf-wallet-dot ${walletAddress ? 'bf-connected' : 'bf-disconnected'}`} />
      <div className={`bf-chain-badge ${chainId === '0x1' ? 'mainnet' : 'other'}`} title={chainId}>
        {getChainLabel(chainId)}
      </div>
      {!walletAddress && hasWallet && (
        <button className="glass-button bf-connect-btn" onClick={connectWallet}>
          Connect
        </button>
      )}
      {walletAddress && (
        <button className="glass-button bf-disconnect-btn" onClick={disconnectWallet}>
          Disconnect
        </button>
      )}
    </div>
  );

  const renderNFTCard = (cid, flipped, onFlip, imgFailed, setImgFailed, altText = 'Christmas Box') => (
    <div className="nft-flip-container">
      <div className={`nft-flip-card ${flipped ? 'is-flipped' : ''}`}>
        <div className="nft-front nft-holo">
          <div
            className="bf-image-wrap"
            onClick={isTouchDevice ? () => onFlip(prev => !prev) : undefined}
            role={isTouchDevice ? 'button' : undefined}
            tabIndex={isTouchDevice ? 0 : undefined}
          >
            <img
              className="bf-image-img"
              alt={altText}
              src={ipfsUrl(cid)}
              onError={() => setImgFailed(true)}
              style={{ display: imgFailed ? 'none' : 'block' }}
            />
            <div className="bf-neon" aria-hidden />
            <div className="bf-shine" aria-hidden />
          </div>
        </div>
        <div className="nft-back">
          <div className="nft-back-content">
            <div style={{ fontWeight: 800 }}>{altText}</div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6 }}>
              Special seasonal contents
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdminMenu = () => (
    <>
      <div
        className="bf-admin-crown"
        role="button"
        tabIndex={0}
        aria-label="Admin menu"
        onClick={() => setShowAdminMenu(s => !s)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowAdminMenu(s => !s);
          }
        }}
      >
        <span className="bf-crown-emoji">🎅</span>
        <span className="bf-admin-dot" />
      </div>
      {showAdminMenu && (
        <div className="bf-admin-menu" onClick={(e) => e.stopPropagation()}>
          <button className="glass-button glass-warning" onClick={() => { /* Retry mints */ }}>
            Retry mints
          </button>
          <button className="glass-button glass-danger" onClick={runDraw}>
            Run draw
          </button>
          <button className="glass-button glass-secondary" onClick={clearTickets}>
            Clear tickets
          </button>
        </div>
      )}
    </>
  );

  const renderMainContent = () => (
    <>
      {!embedded && (
        <div className="christmas-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>🎁 Christmas Special — Gift Box Raffle</h3>
            <div>
              <button
                className="glass-button glass-secondary"
                onClick={async () => {
                  const shareUrl = (() => {
                    try {
                      const sp = new URLSearchParams(window.location.search);
                      if (sp.get('standalone') === '1') return window.location.href;
                    } catch (e) {}
                    return `${window.location.origin}${window.location.pathname}?promo=christmas`;
                  })();

                  const subject = `Christmas Special — Gift Box Raffle`;
                  const body = `Join the Christmas raffle! Buy a box: ${shareUrl}\n\nIncludes: ${RAFFLE_CONFIG.prizeItems.join(', ')}\nPrice: $${RAFFLE_CONFIG.priceUSD}`;

                  if (navigator.share) {
                    try {
                      await navigator.share({ title: subject, text: body, url: shareUrl });
                      setMessage('Shared via native share');
                      return;
                    } catch (e) {
                      // fallthrough to clipboard/email
                    }
                  }

                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    setMessage('Link copied to clipboard — opening email composer...');
                  } catch (e) {
                    setMessage('Opening email composer...');
                  }

                  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                }}
                title="Share link"
              >
                ⬆️ Share
              </button>
            </div>
          </div>

          <p className="bf-small">
            ${RAFFLE_CONFIG.priceUSD} per box — pay with MetaMask. 
            Each purchase gives you an entry and an NFT tied to your wallet.
          </p>
          <div className="stock-status">
            <span className="stock-count">
              Boxes available: {availableBoxes} / {totalBoxes}
            </span>
            {nftStockState.some(n => n.reachable === false) && (
              <span className="stock-warning">
                Some assets unreachable
              </span>
            )}
          </div>
        </div>
      )}

      <div className="bf-top-nav">
        <nav aria-label="Christmas navigation">
          <button
            aria-current={activeTab === 'buy'}
            className={`glass-button ${activeTab === 'buy' ? 'glass-primary' : 'glass-secondary'}`}
            onClick={() => setActiveTab('buy')}
          >
            Buy Box
          </button>
        </nav>
        {!isMobile && (
          <div className="ticket-count">
            {tickets.length} tickets issued
          </div>
        )}
      </div>

      {renderWalletStatus()}

      {!hasWallet && (
        <div className="wallet-alert">
          <strong>🎅 No Wallet Detected</strong>
          <p className="bf-small">
            Install MetaMask to pay with cryptocurrency, or visit us in person to purchase.
          </p>
          <div className="alert-actions">
            <a className="glass-button glass-primary" href="https://metamask.io/download" target="_blank" rel="noreferrer">
              Install MetaMask
            </a>
          </div>
        </div>
      )}

      <div className="bf-storefront">
        {/* Buy Tab */}
        <div className={`bf-hero bf-section ${isMobile && activeTab !== 'buy' ? 'bf-hidden' : 'bf-visible'}`}>
          <div className="hero-content">
            <div className="bf-meta">
              <div className="bf-price">${RAFFLE_CONFIG.priceUSD}</div>
              <div className="bf-sub">per Christmas box</div>
              <div className="bf-prize-value">{RAFFLE_CONFIG.prizeValue}</div>
              <div className="bf-sub">Premium holiday box with exclusive contents</div>
              <div className="bf-sub">NFT assigned on-chain to each purchase</div>
            </div>
            <div className="hero-image">
              {renderNFTCard(
                PURCHASE_NFT_CID,
                cardFlipped,
                setCardFlipped,
                imgFailed,
                setImgFailed,
                'Christmas Gift Box'
              )}
            </div>
          </div>

          <div className="purchase-form">
            <div className="name-inputs">
              <input
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="name-input"
              />
              <input
                type="text"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="name-input"
              />
            </div>
            <button
              className={`blackfriday-button glow ${loading ? 'loading' : ''}`}
              onClick={buyTicket}
              disabled={loading || !firstName || !lastName}
            >
              {loading ? 'Processing…' : `Buy Christmas Box — $${RAFFLE_CONFIG.priceUSD}`}
            </button>
          </div>

          {currentTicket?.nft && (
            <div className="assigned-nft">
              <div className="assigned-label">Your Assigned NFT</div>
              <div className="nft-preview">
                <img
                  src={ipfsUrl(currentTicket.nft)}
                  alt={`Christmas Box #${currentTicket.nftId}`}
                  className="nft-preview-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = ipfsUrl(currentTicket.nft, PUBLIC_IPFS_GATEWAY);
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* (Prizes and Tickets sections removed — only Buy flow shown) */}
      </div>

      <div className="action-status">
        <span className="status-label">Last action:</span>
        <span className="status-message">{message || 'Ready'}</span>
      </div>

      {isLocalAdmin && renderAdminMenu()}

      <button
        className="bf-float-close"
        aria-label="Close raffle"
        onClick={(e) => {
          e.stopPropagation();
          if (isStandalone) {
            try { window.close(); } catch { onClose(); }
          } else {
            onClose();
          }
        }}
      >
        ✕
      </button>
    </>
  );

  const renderMobileBottomNav = () => (
    isMobile && (
      <div
        className="bf-bottom-nav"
        style={{
          position: 'fixed',
          left: 12,
          right: 12,
          bottom: isCompactHeight ? 34 : 12,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <button
          aria-current={activeTab === 'buy'}
          className={`glass-button ${activeTab === 'buy' ? 'glass-primary' : 'glass-secondary'}`}
          onClick={() => {
            setActiveTab('buy');
            // scroll the panel into view and focus the first name input
            setTimeout(() => {
              try {
                panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const firstInput = panelRef.current?.querySelector('.purchase-form .name-input');
                if (firstInput && typeof firstInput.focus === 'function') firstInput.focus();
              } catch (e) {}
            }, 120);
          }}
        >
          🎁 Buy
        </button>
      </div>
    )
  );

  const panel = (
    <div
      ref={panelRef}
      className="christmas-panel"
      data-mobile={isMobile}
      data-inapp={isInAppBrowser}
      data-compact={isCompactHeight}
      onClick={(e) => e.stopPropagation()}
    >
      {renderMainContent()}
    </div>
  );

  if (embedded) return renderMainContent();
  if (inline) return panel;

  return (
    <div
      className="christmas-overlay"
      data-mobile={isMobile}
      data-inapp={isInAppBrowser}
      data-compact={isCompactHeight}
      onClick={() => !isStandalone && onClose()}
    >
      {panel}
      {renderMobileBottomNav()}
    </div>
  );
}